import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase, Calendar, FileText, Gavel, Clock, Plus,
  Upload, Eye, Edit, Trash2, CheckCircle2, AlertCircle,
  Scale, User, MapPin, DollarSign, ArrowLeft, Download,
  MessageSquare, Activity, Timer, AlertTriangle
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';

const CaseDetailsPage = () => {
  const { caseId } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [memos, setMemos] = useState([]);
  const [judgments, setJudgments] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Dialogs
  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddMemo, setShowAddMemo] = useState(false);
  const [showAddJudgment, setShowAddJudgment] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Forms
  const [newSession, setNewSession] = useState({
    session_date: '',
    session_time: '',
    court_name: '',
    judge_name: '',
    notes: ''
  });
  
  const [sessionUpdate, setSessionUpdate] = useState({
    result: '',
    notes: '',
    minutes_document: ''
  });
  
  const [newMemo, setNewMemo] = useState({
    title: '',
    content: '',
    memo_type: 'defense'
  });
  
  const [newJudgment, setNewJudgment] = useState({
    judgment_date: '',
    judgment_text: '',
    judgment_result: 'won',
    document_url: ''
  });
  
  const [docUpload, setDocUpload] = useState({
    title: '',
    category: 'general',
    file: null
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchAllData();
  }, [caseId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [caseRes, sessionsRes, memosRes, judgmentsRes, timelineRes, deadlinesRes] = await Promise.all([
        api.get(`/cases/${caseId}`),
        api.get(`/cases/sessions/${caseId}`),
        api.get(`/cases/memos/${caseId}`),
        api.get(`/cases/judgments/${caseId}`),
        api.get(`/cases/timeline/${caseId}`),
        api.get(`/cases/deadlines/${caseId}`)
      ]);
      
      setCaseData(caseRes.data);
      setSessions(sessionsRes.data);
      setMemos(memosRes.data);
      setJudgments(judgmentsRes.data);
      setTimeline(timelineRes.data);
      setDeadlines(deadlinesRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSession = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cases/sessions', {
        case_id: caseId,
        ...newSession
      });
      toast.success(i18n.language === 'ar' ? 'تم إضافة الجلسة بنجاح' : 'Session added successfully');
      setShowAddSession(false);
      setNewSession({ session_date: '', session_time: '', court_name: '', judge_name: '', notes: '' });
      fetchAllData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة الجلسة' : 'Failed to add session');
    }
  };

  const handleUpdateSession = async (sessionId) => {
    try {
      await api.put(`/cases/sessions/${sessionId}`, sessionUpdate);
      toast.success(i18n.language === 'ar' ? 'تم تحديث نتيجة الجلسة' : 'Session updated successfully');
      setSelectedSession(null);
      setSessionUpdate({ result: '', notes: '', minutes_document: '' });
      fetchAllData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل التحديث' : 'Update failed');
    }
  };

  const handleAddMemo = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cases/memos', {
        case_id: caseId,
        ...newMemo
      });
      toast.success(i18n.language === 'ar' ? 'تم إضافة المذكرة بنجاح' : 'Memo added successfully');
      setShowAddMemo(false);
      setNewMemo({ title: '', content: '', memo_type: 'defense' });
      fetchAllData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة المذكرة' : 'Failed to add memo');
    }
  };

  const handleToggleMemoVisibility = async (memoId, visible) => {
    try {
      await api.put(`/cases/memos/${memoId}/visibility?visible=${visible}`);
      toast.success(i18n.language === 'ar' ? 'تم تحديث الإعدادات' : 'Settings updated');
      fetchAllData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل التحديث' : 'Update failed');
    }
  };

  const handleAddJudgment = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cases/judgments', {
        case_id: caseId,
        ...newJudgment
      });
      toast.success(i18n.language === 'ar' ? 'تم إضافة الحكم بنجاح' : 'Judgment added successfully');
      setShowAddJudgment(false);
      setNewJudgment({ judgment_date: '', judgment_text: '', judgment_result: 'won', document_url: '' });
      fetchAllData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة الحكم' : 'Failed to add judgment');
    }
  };

  const getDeadlineBadge = (deadline) => {
    const daysRemaining = deadline.days_remaining;
    if (daysRemaining < 0) {
      return <Badge className="bg-red-600 text-white">{i18n.language === 'ar' ? 'منتهي' : 'Expired'}</Badge>;
    } else if (daysRemaining <= 7) {
      return <Badge className="bg-orange-500 text-white">{i18n.language === 'ar' ? 'عاجل' : 'Urgent'}</Badge>;
    } else if (daysRemaining <= 15) {
      return <Badge className="bg-yellow-500 text-white">{i18n.language === 'ar' ? 'قريب' : 'Soon'}</Badge>;
    } else {
      return <Badge className="bg-green-500 text-white">{i18n.language === 'ar' ? 'طبيعي' : 'Normal'}</Badge>;
    }
  };

  const getSessionResultBadge = (result) => {
    const results = {
      postponed: { label_ar: 'مؤجلة', color: 'bg-yellow-500' },
      ongoing: { label_ar: 'جاري النظر', color: 'bg-blue-500' },
      judgment_issued: { label_ar: 'صدر حكم', color: 'bg-green-600' },
      settled: { label_ar: 'تصالح', color: 'bg-purple-500' },
      dismissed: { label_ar: 'رفضت', color: 'bg-red-600' },
      accepted: { label_ar: 'قبلت', color: 'bg-green-500' }
    };
    const info = results[result];
    if (!info) return null;
    return <Badge className={`${info.color} text-white`}>{info.label_ar}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{i18n.language === 'ar' ? 'القضية غير موجودة' : 'Case not found'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Back Button & Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">
                {i18n.language === 'ar' ? 'تفاصيل القضية' : 'Case Details'}
              </h1>
              <p className="text-gray-600 mt-1">{caseData.case_number}</p>
            </div>
          </div>

          {/* Case Info Card */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-[#9D7E4F] to-[#B89968] text-white">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl mb-2">{caseData.title}</CardTitle>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className="bg-white/20 text-white">{caseData.case_number}</Badge>
                    <Badge className="bg-white/20 text-white">{caseData.department}</Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm opacity-90 mb-1">{i18n.language === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</p>
                  <p className="font-semibold">{format(new Date(caseData.created_at), 'dd/MM/yyyy')}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'العميل' : 'Client'}</p>
                    <p className="font-semibold text-gray-900">{caseData.client_name}</p>
                  </div>
                </div>
                {caseData.lawyer_name && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Scale className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'المحامي' : 'Lawyer'}</p>
                      <p className="font-semibold text-gray-900">{caseData.lawyer_name}</p>
                    </div>
                  </div>
                )}
                {caseData.court_name && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Gavel className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{i18n.language === 'ar' ? 'المحكمة' : 'Court'}</p>
                      <p className="font-semibold text-gray-900">{caseData.court_name}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2 font-medium">{i18n.language === 'ar' ? 'وصف القضية' : 'Description'}</p>
                <p className="text-gray-900">{caseData.description}</p>
              </div>
            </CardContent>
          </Card>

          {/* Legal Deadlines Alert */}
          {deadlines.length > 0 && (
            <Card className="border-l-4 border-l-orange-500 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle className="w-6 h-6" />
                  {i18n.language === 'ar' ? 'المدد النظامية' : 'Legal Deadlines'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {deadlines.map((deadline) => (
                    <div key={deadline.id} className="p-4 border rounded-lg bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-gray-900">
                          {i18n.language === 'ar' ? 
                            (deadline.deadline_type === 'appeal' ? 'الاستئناف' : 
                             deadline.deadline_type === 'objection' ? 'الاعتراض' : 'التمييز') :
                            deadline.deadline_type
                          }
                        </p>
                        {getDeadlineBadge(deadline)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(deadline.deadline_date), 'dd/MM/yyyy')}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Timer className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-semibold text-orange-600">
                          {deadline.days_remaining < 0 ? 
                            (i18n.language === 'ar' ? 'منتهي' : 'Expired') :
                            `${deadline.days_remaining} ${i18n.language === 'ar' ? 'يوم متبقي' : 'days left'}`
                          }
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="sessions" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="sessions">{i18n.language === 'ar' ? 'الجلسات' : 'Sessions'}</TabsTrigger>
              <TabsTrigger value="memos">{i18n.language === 'ar' ? 'المذكرات' : 'Memos'}</TabsTrigger>
              <TabsTrigger value="judgments">{i18n.language === 'ar' ? 'الأحكام' : 'Judgments'}</TabsTrigger>
              <TabsTrigger value="documents">{i18n.language === 'ar' ? 'المستندات' : 'Documents'}</TabsTrigger>
              <TabsTrigger value="timeline">{i18n.language === 'ar' ? 'Timeline' : 'Timeline'}</TabsTrigger>
            </TabsList>

            {/* Sessions Tab */}
            <TabsContent value="sessions">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{i18n.language === 'ar' ? 'الجلسات' : 'Sessions'}</CardTitle>
                    <Dialog open={showAddSession} onOpenChange={setShowAddSession}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary gap-2">
                          <Plus className="w-4 h-4" />
                          {i18n.language === 'ar' ? 'إضافة جلسة' : 'Add Session'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{i18n.language === 'ar' ? 'إضافة جلسة جديدة' : 'Add New Session'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddSession} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>{i18n.language === 'ar' ? 'تاريخ الجلسة' : 'Session Date'}</Label>
                              <Input
                                type="date"
                                value={newSession.session_date}
                                onChange={(e) => setNewSession({...newSession, session_date: e.target.value})}
                                required
                              />
                            </div>
                            <div>
                              <Label>{i18n.language === 'ar' ? 'وقت الجلسة' : 'Time'}</Label>
                              <Input
                                type="time"
                                value={newSession.session_time}
                                onChange={(e) => setNewSession({...newSession, session_time: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>{i18n.language === 'ar' ? 'المحكمة' : 'Court'}</Label>
                              <Input
                                value={newSession.court_name}
                                onChange={(e) => setNewSession({...newSession, court_name: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>{i18n.language === 'ar' ? 'القاضي' : 'Judge'}</Label>
                              <Input
                                value={newSession.judge_name}
                                onChange={(e) => setNewSession({...newSession, judge_name: e.target.value})}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>{i18n.language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                              <Textarea
                                value={newSession.notes}
                                onChange={(e) => setNewSession({...newSession, notes: e.target.value})}
                                rows={3}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowAddSession(false)}>
                              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                            </Button>
                            <Button type="submit" className="btn-primary">
                              {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {sessions.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">{i18n.language === 'ar' ? 'لا توجد جلسات' : 'No sessions'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sessions.map((session) => (
                        <div key={session.id} className="p-4 border rounded-lg hover:shadow-md transition-all">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-bold text-gray-900">
                                  {i18n.language === 'ar' ? 'الجلسة' : 'Session'} #{session.session_number}
                                </h4>
                                {session.result && getSessionResultBadge(session.result)}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {format(new Date(session.session_date), 'dd/MM/yyyy')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {session.session_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {session.court_name}
                                </span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedSession(session.id)}
                            >
                              {i18n.language === 'ar' ? 'تسجيل النتيجة' : 'Record Result'}
                            </Button>
                          </div>
                          {session.notes && (
                            <p className="text-sm text-gray-700 mt-2">{session.notes}</p>
                          )}
                          {session.minutes_document && (
                            <Button size="sm" variant="link" className="mt-2">
                              <Download className="w-4 h-4 ml-1" />
                              {i18n.language === 'ar' ? 'تحميل الضبط' : 'Download Minutes'}
                            </Button>
                          )}
                          
                          {/* Session Update Dialog */}
                          {selectedSession === session.id && (
                            <Dialog open={true} onOpenChange={() => setSelectedSession(null)}>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>{i18n.language === 'ar' ? 'تسجيل نتيجة الجلسة' : 'Record Session Result'}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label>{i18n.language === 'ar' ? 'النتيجة' : 'Result'}</Label>
                                    <Select
                                      value={sessionUpdate.result}
                                      onValueChange={(value) => setSessionUpdate({...sessionUpdate, result: value})}
                                    >
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="postponed">{i18n.language === 'ar' ? 'مؤجلة' : 'Postponed'}</SelectItem>
                                        <SelectItem value="ongoing">{i18n.language === 'ar' ? 'جاري النظر' : 'Ongoing'}</SelectItem>
                                        <SelectItem value="judgment_issued">{i18n.language === 'ar' ? 'صدر حكم' : 'Judgment Issued'}</SelectItem>
                                        <SelectItem value="settled">{i18n.language === 'ar' ? 'تصالح' : 'Settled'}</SelectItem>
                                        <SelectItem value="dismissed">{i18n.language === 'ar' ? 'رفضت' : 'Dismissed'}</SelectItem>
                                        <SelectItem value="accepted">{i18n.language === 'ar' ? 'قبلت' : 'Accepted'}</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>{i18n.language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                                    <Textarea
                                      value={sessionUpdate.notes}
                                      onChange={(e) => setSessionUpdate({...sessionUpdate, notes: e.target.value})}
                                      rows={4}
                                    />
                                  </div>
                                  <div>
                                    <Label>{i18n.language === 'ar' ? 'رابط ضبط الجلسة' : 'Minutes Document URL'}</Label>
                                    <Input
                                      value={sessionUpdate.minutes_document}
                                      onChange={(e) => setSessionUpdate({...sessionUpdate, minutes_document: e.target.value})}
                                    />
                                  </div>
                                  <div className="flex justify-end gap-3">
                                    <Button variant="outline" onClick={() => setSelectedSession(null)}>
                                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                                    </Button>
                                    <Button className="btn-primary" onClick={() => handleUpdateSession(session.id)}>
                                      {i18n.language === 'ar' ? 'حفظ' : 'Save'}
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Memos Tab */}
            <TabsContent value="memos">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{i18n.language === 'ar' ? 'المذكرات القانونية' : 'Legal Memos'}</CardTitle>
                    <Dialog open={showAddMemo} onOpenChange={setShowAddMemo}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary gap-2">
                          <Plus className="w-4 h-4" />
                          {i18n.language === 'ar' ? 'إضافة مذكرة' : 'Add Memo'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>{i18n.language === 'ar' ? 'إضافة مذكرة قانونية' : 'Add Legal Memo'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddMemo} className="space-y-4">
                          <div>
                            <Label>{i18n.language === 'ar' ? 'عنوان المذكرة' : 'Memo Title'}</Label>
                            <Input
                              value={newMemo.title}
                              onChange={(e) => setNewMemo({...newMemo, title: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <Label>{i18n.language === 'ar' ? 'نوع المذكرة' : 'Memo Type'}</Label>
                            <Select
                              value={newMemo.memo_type}
                              onValueChange={(value) => setNewMemo({...newMemo, memo_type: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="defense">{i18n.language === 'ar' ? 'مذكرة دفاع' : 'Defense'}</SelectItem>
                                <SelectItem value="prosecution">{i18n.language === 'ar' ? 'مذكرة ادعاء' : 'Prosecution'}</SelectItem>
                                <SelectItem value="reply">{i18n.language === 'ar' ? 'مذكرة رد' : 'Reply'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>{i18n.language === 'ar' ? 'محتوى المذكرة' : 'Content'}</Label>
                            <Textarea
                              value={newMemo.content}
                              onChange={(e) => setNewMemo({...newMemo, content: e.target.value})}
                              rows={8}
                              required
                            />
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowAddMemo(false)}>
                              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                            </Button>
                            <Button type="submit" className="btn-primary">
                              {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {memos.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">{i18n.language === 'ar' ? 'لا توجد مذكرات' : 'No memos'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {memos.map((memo) => (
                        <div key={memo.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-gray-900 mb-2">{memo.title}</h4>
                              <div className="flex items-center gap-2 text-sm">
                                <Badge variant="outline">{memo.memo_type}</Badge>
                                <span className="text-gray-600">
                                  {format(new Date(memo.created_at), 'dd/MM/yyyy')}
                                </span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant={memo.visible_to_client ? "default" : "outline"}
                              onClick={() => handleToggleMemoVisibility(memo.id, !memo.visible_to_client)}
                            >
                              {memo.visible_to_client ? 
                                (i18n.language === 'ar' ? 'مرئية للعميل' : 'Visible to Client') :
                                (i18n.language === 'ar' ? 'داخلية فقط' : 'Internal Only')
                              }
                            </Button>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{memo.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Judgments Tab */}
            <TabsContent value="judgments">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{i18n.language === 'ar' ? 'الأحكام' : 'Judgments'}</CardTitle>
                    <Dialog open={showAddJudgment} onOpenChange={setShowAddJudgment}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary gap-2">
                          <Plus className="w-4 h-4" />
                          {i18n.language === 'ar' ? 'إضافة حكم' : 'Add Judgment'}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>{i18n.language === 'ar' ? 'إضافة حكم' : 'Add Judgment'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddJudgment} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>{i18n.language === 'ar' ? 'تاريخ الحكم' : 'Judgment Date'}</Label>
                              <Input
                                type="date"
                                value={newJudgment.judgment_date}
                                onChange={(e) => setNewJudgment({...newJudgment, judgment_date: e.target.value})}
                                required
                              />
                            </div>
                            <div>
                              <Label>{i18n.language === 'ar' ? 'نتيجة الحكم' : 'Result'}</Label>
                              <Select
                                value={newJudgment.judgment_result}
                                onValueChange={(value) => setNewJudgment({...newJudgment, judgment_result: value})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="won">{i18n.language === 'ar' ? 'مكسوب' : 'Won'}</SelectItem>
                                  <SelectItem value="lost">{i18n.language === 'ar' ? 'مخسور' : 'Lost'}</SelectItem>
                                  <SelectItem value="partial">{i18n.language === 'ar' ? 'جزئي' : 'Partial'}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2">
                              <Label>{i18n.language === 'ar' ? 'نص الحكم' : 'Judgment Text'}</Label>
                              <Textarea
                                value={newJudgment.judgment_text}
                                onChange={(e) => setNewJudgment({...newJudgment, judgment_text: e.target.value})}
                                rows={6}
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <Label>{i18n.language === 'ar' ? 'رابط المستند' : 'Document URL'}</Label>
                              <Input
                                value={newJudgment.document_url}
                                onChange={(e) => setNewJudgment({...newJudgment, document_url: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowAddJudgment(false)}>
                              {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                            </Button>
                            <Button type="submit" className="btn-primary">
                              {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {judgments.length === 0 ? (
                    <div className="text-center py-12">
                      <Gavel className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">{i18n.language === 'ar' ? 'لا توجد أحكام' : 'No judgments'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {judgments.map((judgment) => (
                        <div key={judgment.id} className="p-6 border rounded-lg bg-gradient-to-r from-white to-gray-50">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h4 className="text-xl font-bold text-gray-900 mb-2">
                                {i18n.language === 'ar' ? 'الحكم رقم' : 'Judgment'} {judgment.judgment_number}
                              </h4>
                              <div className="flex items-center gap-3">
                                <Badge className={
                                  judgment.judgment_result === 'won' ? 'bg-green-600' :
                                  judgment.judgment_result === 'lost' ? 'bg-red-600' : 'bg-yellow-600'
                                }>
                                  {judgment.judgment_result === 'won' ? (i18n.language === 'ar' ? 'مكسوب' : 'Won') :
                                   judgment.judgment_result === 'lost' ? (i18n.language === 'ar' ? 'مخسور' : 'Lost') :
                                   (i18n.language === 'ar' ? 'جزئي' : 'Partial')}
                                </Badge>
                                <span className="text-sm text-gray-600">
                                  {format(new Date(judgment.judgment_date), 'dd/MM/yyyy')}
                                </span>
                              </div>
                            </div>
                            {judgment.document_url && (
                              <Button size="sm" variant="outline">
                                <Download className="w-4 h-4 ml-1" />
                                {i18n.language === 'ar' ? 'تحميل' : 'Download'}
                              </Button>
                            )}
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap mb-4">{judgment.judgment_text}</p>
                          
                          {/* Deadlines for this judgment */}
                          <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t">
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <p className="text-xs text-blue-600 font-semibold mb-1">
                                {i18n.language === 'ar' ? 'موعد الاستئناف' : 'Appeal Deadline'}
                              </p>
                              <p className="text-sm font-bold text-blue-700">
                                {format(new Date(judgment.appeal_deadline), 'dd/MM/yyyy')}
                              </p>
                              <p className="text-xs text-blue-600 mt-1">
                                {differenceInDays(new Date(judgment.appeal_deadline), new Date())} {i18n.language === 'ar' ? 'يوم' : 'days'}
                              </p>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg">
                              <p className="text-xs text-orange-600 font-semibold mb-1">
                                {i18n.language === 'ar' ? 'موعد الاعتراض' : 'Objection Deadline'}
                              </p>
                              <p className="text-sm font-bold text-orange-700">
                                {format(new Date(judgment.objection_deadline), 'dd/MM/yyyy')}
                              </p>
                              <p className="text-xs text-orange-600 mt-1">
                                {differenceInDays(new Date(judgment.objection_deadline), new Date())} {i18n.language === 'ar' ? 'يوم' : 'days'}
                              </p>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg">
                              <p className="text-xs text-purple-600 font-semibold mb-1">
                                {i18n.language === 'ar' ? 'موعد التمييز' : 'Cassation Deadline'}
                              </p>
                              <p className="text-sm font-bold text-purple-700">
                                {format(new Date(judgment.cassation_deadline), 'dd/MM/yyyy')}
                              </p>
                              <p className="text-xs text-purple-600 mt-1">
                                {differenceInDays(new Date(judgment.cassation_deadline), new Date())} {i18n.language === 'ar' ? 'يوم' : 'days'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{i18n.language === 'ar' ? 'المستندات' : 'Documents'}</CardTitle>
                    <Button className="btn-primary gap-2">
                      <Upload className="w-4 h-4" />
                      {i18n.language === 'ar' ? 'رفع مستند' : 'Upload Document'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">{i18n.language === 'ar' ? 'سيتم إضافة نظام رفع المستندات قريباً' : 'Document upload system coming soon'}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-6 h-6" />
                    {i18n.language === 'ar' ? 'Timeline - سجل إجراءات القضية' : 'Case Timeline'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {timeline.length === 0 ? (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">{i18n.language === 'ar' ? 'لا توجد أحداث' : 'No events'}</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Timeline Line */}
                      <div className="absolute right-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                      
                      <div className="space-y-6">
                        {timeline.map((event, index) => (
                          <div key={event.id} className="relative flex gap-4">
                            {/* Timeline Dot */}
                            <div className="relative z-10 flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9D7E4F] to-[#B89968] flex items-center justify-center shadow-lg">
                                <Activity className="w-4 h-4 text-white" />
                              </div>
                            </div>
                            
                            {/* Event Content */}
                            <div className="flex-1 pb-6">
                              <div className="bg-white p-4 rounded-lg shadow-md border border-gray-100">
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="font-bold text-gray-900">{event.title}</h4>
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(event.created_at), 'dd/MM/yyyy HH:mm')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mb-2">{event.description}</p>
                                <p className="text-xs text-gray-500">
                                  {i18n.language === 'ar' ? 'بواسطة:' : 'By:'} {event.user_name}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CaseDetailsPage;